/**
 * Data layer for the COMPANY panel's Mood Tracking section.
 *
 * The source is the Mood Log check-in record — emotion + context, one document
 * per logged moment. Two things about it shape every number here:
 *
 *  - Every emotion belongs to a BAND (green / amber / red). A check-in takes
 *    the band of its primary emotion, so the Positive / Mixed / Difficult mix
 *    always sums to the check-in count. That mix is the headline reading.
 *  - Days and hours come from the wall clock the client sent, not from UTC, so
 *    "checked in at 11pm" means 11pm where the person was.
 *
 * The note attached to a check-in is encrypted special-category health data and
 * the photo is a private object. Neither is fetched by any endpoint below, so
 * neither can reach this layer.
 *
 * Backed by the company/v1 mood-hub endpoints (token-scoped to the company; the
 * B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes an
 * optional `department` so the section's filter can narrow the whole page.
 *
 * The adapter is exported so a super-admin Mood hub, served by the same
 * builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyMoodDashboard,
  getCompanyMoodUsers,
  getCompanyMoodUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Check-in block shared by every Mood dashboard. */
export const adaptMoodCheckIns = (data) => ({
  loggers: data.loggers ?? 0,
  checkIns: data.checkIns ?? 0,
  loggedDays: data.loggedDays ?? 0,
  avgCheckInsPerUser: data.avgCheckInsPerUser ?? 0,
  avgCheckInsPerDay: data.avgCheckInsPerDay ?? 0,
  avgDaysPerUser: data.avgDaysPerUser ?? 0,
  withPhoto: data.withPhoto ?? 0,
  withSecondEmotion: data.withSecondEmotion ?? 0,
  firstCheckInDate: data.firstCheckInDate || null,
  lastCheckInDate: data.lastCheckInDate || null,
  weeklyReviews: data.weeklyReviews ?? 0,

  bandSplit: data.bandSplit || [],
  positiveRate: data.positiveRate ?? 0,
  mixedRate: data.mixedRate ?? 0,
  difficultRate: data.difficultRate ?? 0,

  emotionsNamed: data.emotionsNamed ?? 0,
  emotionsAvailable: data.emotionsAvailable ?? 0,
  positiveEmotions: data.positiveEmotions || [],
  difficultEmotions: data.difficultEmotions || [],

  frequency: data.frequency || [],

  // Backend zero-fills the last 30 days; charts consume plain series. The three
  // band series are stacked, so together they are the check-in total.
  checkInsTrend: (data.activityTrend || []).map((day) => day.checkIns || 0),
  loggersTrend: (data.activityTrend || []).map((day) => day.loggers || 0),
  positiveTrend: (data.activityTrend || []).map((day) => day.green || 0),
  mixedTrend: (data.activityTrend || []).map((day) => day.amber || 0),
  difficultTrend: (data.activityTrend || []).map((day) => day.red || 0),

  weekdayCheckIns: data.weekdayCheckIns || [],
  hourlyCheckIns: data.hourlyCheckIns || [],
  dayParts: data.dayParts || [],

  // Each entry is { label, total, positiveRate, difficultRate, bands[] } —
  // one context option with its own band mix.
  byActivity: data.byActivity || [],
  byCompanion: data.byCompanion || [],
  byLocation: data.byLocation || []
});

export const fetchCompanyMoodDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyMoodDashboard(department));
    return {
      ...adaptMoodCheckIns(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees track their mood".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyMoodDashboard error:', error);
    return null;
  }
};

// Matches the MoodUsersTable fetcher contract.
export const fetchCompanyMoodUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyMoodUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyMoodUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's mood profile ({ user, bandSplit, topEmotions,
 * byLocation, byActivity, dailyActivity }). Volume, band mix and named emotions
 * only — no notes, no photos and no individual check-in rows. The backend 404s
 * any user outside this company, so no scoping is needed here.
 */
export const fetchCompanyMoodUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyMoodUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyMoodUserDetail error:', error);
    return null;
  }
};
